import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { rowClass, StatusPill } from "./Primitives";
import { formatTime12, formatLongDate } from "../../lib/format";

/**
 * Threaded rows view for Calling Activity.
 *
 * Groups entries by contact_id (falling back to a "individual · company" key)
 * so multiple touches to the same person stack under one expandable parent row.
 */
export default function ThreadedRows({ rows, onRowClick }) {
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const dataRows = rows.filter((r) => !r._now);
  if (dataRows.length === 0) {
    return (
      <div className="py-6 text-center text-neutral-400 text-sm">
        No entries — click Add to log one.
      </div>
    );
  }

  // Group by contact identity
  const keyFor = (r) => {
    if (r.contact_id) return `cid:${r.contact_id}`;
    const indv = (r.individual || "").trim().toLowerCase();
    const comp = (r.company || "").trim().toLowerCase();
    if (indv || comp) return `nc:${indv}|${comp}`;
    return `id:${r.id}`;
  };

  const groups = {};
  for (const r of dataRows) {
    const k = keyFor(r);
    if (!groups[k]) groups[k] = [];
    groups[k].push(r);
  }
  // Sort each group's rows newest first
  Object.values(groups).forEach((arr) =>
    arr.sort((a, b) => (b.date || "").localeCompare(a.date || ""))
  );
  // Order groups by their latest touch (most recent first)
  const groupList = Object.entries(groups).sort(
    ([, a], [, b]) =>
      (b[0]?.date || "").localeCompare(a[0]?.date || "") ||
      (b[0]?.time || "").localeCompare(a[0]?.time || "")
  );

  const toggle = (k) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-neutral-500 font-semibold bg-[var(--rw-surface-2)]">
          <th className="py-2 px-3 w-6" />
          <th className="py-2 px-3">Individual</th>
          <th className="py-2 px-3">Company</th>
          <th className="py-2 px-3">Touches</th>
          <th className="py-2 px-3">Latest</th>
          <th className="py-2 px-3">Latest Outcome</th>
          <th className="py-2 px-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {groupList.map(([k, entries]) => {
          const isOpen = openKeys.has(k);
          const latest = entries[0];
          const label =
            (latest.individual || "").trim() || "(unspecified)";
          return (
            <React.Fragment key={k}>
              <tr
                className={`border-t border-[var(--rw-border)] cursor-pointer hover:bg-[var(--rw-surface-2)] ${rowClass(latest)}`}
                onClick={() => toggle(k)}
                data-testid={`thread-parent-${k}`}
              >
                <td className="py-2 px-3 text-neutral-500">
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </td>
                <td className="py-2 px-3 font-semibold">{label}</td>
                <td className="py-2 px-3">{latest.company || "—"}</td>
                <td className="py-2 px-3 font-mono-rw text-xs">
                  {entries.length}
                </td>
                <td className="py-2 px-3 font-mono-rw text-xs">
                  {latest.date ? formatLongDate(latest.date) : "—"}
                </td>
                <td className="py-2 px-3">{latest.outcome || latest.purpose || "—"}</td>
                <td className="py-2 px-3"><StatusPill value={latest.status} /></td>
              </tr>
              {isOpen &&
                entries.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t border-[var(--rw-border)] bg-[var(--rw-surface-2)]/40 cursor-pointer hover:bg-[var(--rw-surface-2)] ${rowClass(r)}`}
                    onClick={() => onRowClick && onRowClick(r)}
                    data-testid={`thread-child-${r.id}`}
                  >
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3 pl-8 text-xs text-neutral-600" colSpan={2}>
                      {r.date ? formatLongDate(r.date) : ""}{" "}
                      {r.time ? <span className="font-mono-rw ml-1">{formatTime12(r.time)}</span> : null}
                      {r.notes ? (
                        <div className="text-neutral-500 mt-0.5 line-clamp-2">{r.notes}</div>
                      ) : null}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {r.call_type || r.purpose || "—"}
                    </td>
                    <td className="py-2 px-3 text-xs">{r.duration ? `${r.duration} min` : ""}</td>
                    <td className="py-2 px-3 text-xs">{r.outcome || "—"}</td>
                    <td className="py-2 px-3"><StatusPill value={r.status} /></td>
                  </tr>
                ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
